import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Pricing } from '../../../../../types/cms.types';
import './CpnPricingForm.css';

interface CpnPricingFormProps {
    visible: boolean;
    onHide: () => void;
    pricing?: Pricing | null;
    onSave: (pricing: Omit<Pricing, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
    provinces: string[];
    fixedVehicleType?: 'car' | 'motorbike'; // Loại xe cố định
    fixedCarSeats?: number | null; // Số chỗ ngồi cố định
}

export const CpnPricingForm: React.FC<CpnPricingFormProps> = ({
    visible,
    onHide,
    pricing,
    onSave,
    provinces,
    fixedVehicleType,
    fixedCarSeats
}) => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Omit<Pricing, 'id' | 'created_at' | 'updated_at'>>({
        province: '',
        vehicle_type: 'motorbike',
        car_seats: null,
        base_price: 0,
        price_2_to_10km: 0,
        price_11_to_20km: 0,
        price_over_20km: 0,
        price_over_50km: 0,
        price_over_100km: 0,
        service_fee: 0,
        fee_after_21h: 0,
        fee_after_0h: 0,
        fee_peak_hours: 0,
        fee_rainy: 0,
        price_per_minute: 0
    });

    const provinceOptions = provinces.map(province => ({
        label: province,
        value: province
    }));

    const vehicleTypeOptions = [
        { label: 'Xe máy', value: 'motorbike' },
        { label: 'Ô tô', value: 'car' }
    ];

    const carSeatsOptions = [
        { label: '4 chỗ', value: 4 },
        { label: '7 chỗ', value: 7 }
    ];

    // Reset form khi dialog mở/đóng hoặc khi pricing thay đổi
    useEffect(() => {
        if (visible) {
            if (pricing) {
                // Edit mode
                setFormData({
                    province: pricing.province || '',
                    vehicle_type: pricing.vehicle_type || fixedVehicleType || 'motorbike',
                    car_seats: pricing.car_seats ?? fixedCarSeats,
                    base_price: pricing.base_price || 0,
                    price_2_to_10km: pricing.price_2_to_10km || 0,
                    price_11_to_20km: pricing.price_11_to_20km || 0,
                    price_over_20km: pricing.price_over_20km || 0,
                    price_over_50km: pricing.price_over_50km || 0,
                    price_over_100km: pricing.price_over_100km || 0,
                    service_fee: pricing.service_fee || 0,
                    fee_after_21h: pricing.fee_after_21h || 0,
                    fee_after_0h: pricing.fee_after_0h || 0,
                    fee_peak_hours: pricing.fee_peak_hours || 0,
                    fee_rainy: pricing.fee_rainy || 0,
                    price_per_minute: pricing.price_per_minute || 0
                });
            } else {
                // Create mode - sử dụng loại xe cố định nếu có
                setFormData({
                    province: '',
                    vehicle_type: fixedVehicleType || 'motorbike',
                    car_seats: fixedCarSeats ?? null,
                    base_price: 0,
                    price_2_to_10km: 0,
                    price_11_to_20km: 0,
                    price_over_20km: 0,
                    price_over_50km: 0,
                    price_over_100km: 0,
                    service_fee: 0,
                    fee_after_21h: 0,
                    fee_after_0h: 0,
                    fee_peak_hours: 0,
                    fee_rainy: 0,
                    price_per_minute: 0
                });
            }
        }
    }, [visible, pricing, fixedVehicleType, fixedCarSeats]);

    // Xử lý thay đổi loại xe
    const handleVehicleTypeChange = (value: 'car' | 'motorbike') => {
        setFormData(prev => ({
            ...prev,
            vehicle_type: value,
            car_seats: value === 'motorbike' ? null : 4 // Default to 4 seats for car
        }));
    };

    const handleSave = async () => {
        try {
            // Validation
            if (!formData.province.trim()) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Lỗi',
                    detail: 'Vui lòng chọn tỉnh thành'
                });
                return;
            }

            if (formData.vehicle_type === 'car' && !formData.car_seats && fixedCarSeats === undefined) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Lỗi',
                    detail: 'Vui lòng chọn số chỗ ngồi cho xe ô tô'
                });
                return;
            }

            if (formData.base_price <= 0) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Lỗi',
                    detail: 'Giá cơ bản phải lớn hơn 0'
                });
                return;
            }

            setLoading(true);
            await onSave(formData);

            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: pricing ? 'Cập nhật bảng giá thành công' : 'Tạo bảng giá thành công'
            });

            onHide();
        } catch (error) {
            console.error('Error saving pricing:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi lưu dữ liệu'
            });
        } finally {
            setLoading(false);
        }
    };

    const isEditMode = !!pricing;

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                style={{ width: '90vw', maxWidth: '800px' }}
                header={isEditMode ? 'Chỉnh sửa bảng giá' : 'Tạo bảng giá mới'}
                modal
                className="p-fluid"
                onHide={onHide}
                footer={
                    <div className="p-dialog-footer">
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={onHide}
                            disabled={loading}
                        />
                        <Button
                            label={isEditMode ? 'Cập nhật' : 'Tạo mới'}
                            icon="pi pi-check"
                            loading={loading}
                            onClick={handleSave}
                        />
                    </div>
                }
            >
                <div className="pricing-form">
                    <div className="p-fluid formgrid grid">
                        {/* Thông tin cơ bản */}
                        <div className="field col-12">
                            <h3>Thông tin cơ bản</h3>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="province">Tỉnh thành *</label>
                            <Dropdown
                                id="province"
                                value={formData.province}
                                options={provinceOptions}
                                onChange={(e) => setFormData(prev => ({ ...prev, province: e.value }))}
                                placeholder="Chọn tỉnh thành"
                                filter
                                filterPlaceholder="Tìm kiếm tỉnh thành"
                            />
                        </div>

                        {!fixedVehicleType && (
                            <div className="field col-12 md:col-6">
                                <label htmlFor="vehicle_type">Loại xe *</label>
                                <Dropdown
                                    id="vehicle_type"
                                    value={formData.vehicle_type}
                                    options={vehicleTypeOptions}
                                    onChange={(e) => handleVehicleTypeChange(e.value)}
                                    placeholder="Chọn loại xe"
                                />
                            </div>
                        )}

                        {fixedVehicleType && (
                            <div className="field col-12 md:col-6">
                                <label htmlFor="vehicle_type_display">Loại xe</label>
                                <div className="p-inputtext" style={{ padding: '0.75rem 1rem', background: '#f8f9fa', color: '#6c757d' }}>
                                    {fixedVehicleType === 'motorbike' ? 'Xe máy' : 'Ô tô'}
                                </div>
                            </div>
                        )}

                        {formData.vehicle_type === 'car' && fixedCarSeats === undefined && (
                            <div className="field col-12 md:col-6">
                                <label htmlFor="car_seats">Số chỗ ngồi *</label>
                                <Dropdown
                                    id="car_seats"
                                    value={formData.car_seats}
                                    options={carSeatsOptions}
                                    onChange={(e) => setFormData(prev => ({ ...prev, car_seats: e.value }))}
                                    placeholder="Chọn số chỗ ngồi"
                                />
                            </div>
                        )}

                        {formData.vehicle_type === 'car' && fixedCarSeats !== undefined && (
                            <div className="field col-12 md:col-6">
                                <label htmlFor="car_seats_display">Số chỗ ngồi</label>
                                <div className="p-inputtext" style={{ padding: '0.75rem 1rem', background: '#f8f9fa', color: '#6c757d' }}>
                                    {fixedCarSeats} chỗ
                                </div>
                            </div>
                        )}

                        {/* Giá theo khoảng cách */}
                        <div className="field col-12">
                            <h3>Giá theo khoảng cách</h3>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="base_price">Giá cơ bản (0-2km) *</label>
                            <InputNumber
                                id="base_price"
                                value={formData.base_price}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, base_price: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_2_to_10km">Giá 2-10km *</label>
                            <InputNumber
                                id="price_2_to_10km"
                                value={formData.price_2_to_10km}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_2_to_10km: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_11_to_20km">Giá 11-20km *</label>
                            <InputNumber
                                id="price_11_to_20km"
                                value={formData.price_11_to_20km}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_11_to_20km: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_over_20km">Giá trên 20km *</label>
                            <InputNumber
                                id="price_over_20km"
                                value={formData.price_over_20km}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_over_20km: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_over_50km">Giá trên 50km *</label>
                            <InputNumber
                                id="price_over_50km"
                                value={formData.price_over_50km}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_over_50km: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_over_100km">Giá trên 100km *</label>
                            <InputNumber
                                id="price_over_100km"
                                value={formData.price_over_100km}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_over_100km: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        {/* Phí dịch vụ và phụ thu */}
                        <div className="field col-12">
                            <h3>Phí dịch vụ và phụ thu</h3>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="service_fee">Phí dịch vụ *</label>
                            <InputNumber
                                id="service_fee"
                                value={formData.service_fee}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, service_fee: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="price_per_minute">Giá theo phút *</label>
                            <InputNumber
                                id="price_per_minute"
                                value={formData.price_per_minute}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, price_per_minute: e.value || 0 }))}
                                mode="currency"
                                currency="VND"
                                locale="vi-VN"
                                minFractionDigits={0}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="fee_after_21h">Phí sau 21h (%)</label>
                            <InputNumber
                                id="fee_after_21h"
                                value={formData.fee_after_21h}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, fee_after_21h: e.value || 0 }))}
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="fee_after_0h">Phí sau 0h (%)</label>
                            <InputNumber
                                id="fee_after_0h"
                                value={formData.fee_after_0h}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, fee_after_0h: e.value || 0 }))}
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="fee_peak_hours">Phí giờ cao điểm (%)</label>
                            <InputNumber
                                id="fee_peak_hours"
                                value={formData.fee_peak_hours}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, fee_peak_hours: e.value || 0 }))}
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label htmlFor="fee_rainy">Phí trời mưa (%)</label>
                            <InputNumber
                                id="fee_rainy"
                                value={formData.fee_rainy}
                                onValueChange={(e) => setFormData(prev => ({ ...prev, fee_rainy: e.value || 0 }))}
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
